/**
 * Domain Layer - Error Types
 * ドメインエラー型定義
 */

/**
 * CSVパースエラーの種類
 */
export const CsvParseErrorType = {
  /** 無効なファイル形式 */
  INVALID_FORMAT: 'INVALID_FORMAT',
  /** 無効なヘッダー */
  INVALID_HEADERS: 'INVALID_HEADERS',
  /** パース失敗 */
  PARSE_FAILED: 'PARSE_FAILED',
  /** 空ファイル */
  EMPTY_FILE: 'EMPTY_FILE',
  /** ファイルサイズ超過 */
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
} as const;

export type CsvParseErrorType = typeof CsvParseErrorType[keyof typeof CsvParseErrorType];

/**
 * CSVパースエラー
 */
export interface CsvParseError {
  readonly type: CsvParseErrorType;
  readonly message: string;
  readonly details?: string;
  readonly line?: number;
}

/**
 * データ処理エラーの種類
 */
export const ProcessingErrorType = {
  /** APIエラー */
  API_ERROR: 'API_ERROR',
  /** バリデーションエラー */
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  /** タイムアウト */
  TIMEOUT: 'TIMEOUT',
  /** 変換エラー */
  CONVERSION_ERROR: 'CONVERSION_ERROR',
} as const;

export type ProcessingErrorType = typeof ProcessingErrorType[keyof typeof ProcessingErrorType];

/**
 * データ処理エラー
 */
export interface ProcessingError {
  readonly type: ProcessingErrorType;
  readonly message: string;
  readonly details?: string;
  readonly rowIndex?: number;
}

/**
 * 登録エラーの種類
 */
export const RegistrationErrorType = {
  /** APIエラー */
  API_ERROR: 'API_ERROR',
  /** 重複エラー */
  DUPLICATE: 'DUPLICATE',
  /** バリデーションエラー */
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  /** 認証エラー */
  AUTH_ERROR: 'AUTH_ERROR',
  /** ネットワークエラー */
  NETWORK_ERROR: 'NETWORK_ERROR',
} as const;

export type RegistrationErrorType = typeof RegistrationErrorType[keyof typeof RegistrationErrorType];

/**
 * 登録エラー
 */
export interface RegistrationError {
  readonly type: RegistrationErrorType;
  readonly message: string;
  readonly details?: string;
  readonly affectedRows?: readonly number[];
}

/**
 * エクスポートエラーの種類
 */
export const ExportErrorType = {
  /** APIエラー */
  API_ERROR: 'API_ERROR',
  /** 認証エラー */
  AUTH_ERROR: 'AUTH_ERROR',
  /** クォータ超過 */
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
  /** スプレッドシートが見つからない */
  SPREADSHEET_NOT_FOUND: 'SPREADSHEET_NOT_FOUND',
  /** 書き込み権限がない */
  PERMISSION_DENIED: 'PERMISSION_DENIED',
} as const;

export type ExportErrorType = typeof ExportErrorType[keyof typeof ExportErrorType];

/**
 * エクスポートエラー
 */
export interface ExportError {
  readonly type: ExportErrorType;
  readonly message: string;
  readonly details?: string;
  readonly code?: string;
}

/**
 * Result型（成功または失敗を表す）
 */
export type Result<T, E> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: E };

/**
 * 成功のResultを生成する
 * @param value 成功値
 * @returns 成功のResult
 */
export const ok = <T, E>(value: T): Result<T, E> => ({
  ok: true,
  value,
});

/**
 * 失敗のResultを生成する
 * @param error エラー
 * @returns 失敗のResult
 */
export const err = <T, E>(error: E): Result<T, E> => ({
  ok: false,
  error,
});

/**
 * CsvParseErrorを生成する
 * @param type エラータイプ
 * @param message メッセージ
 * @param details 詳細
 * @param line 行番号
 * @returns CsvParseError
 */
export const createCsvParseError = (
  type: CsvParseErrorType,
  message: string,
  details?: string,
  line?: number
): CsvParseError => ({
  type,
  message,
  details,
  line,
});

/**
 * ProcessingErrorを生成する
 * @param type エラータイプ
 * @param message メッセージ
 * @param details 詳細
 * @param rowIndex 行インデックス
 * @returns ProcessingError
 */
export const createProcessingError = (
  type: ProcessingErrorType,
  message: string,
  details?: string,
  rowIndex?: number
): ProcessingError => ({
  type,
  message,
  details,
  rowIndex,
});

/**
 * RegistrationErrorを生成する
 * @param type エラータイプ
 * @param message メッセージ
 * @param details 詳細
 * @param affectedRows 影響を受けた行
 * @returns RegistrationError
 */
export const createRegistrationError = (
  type: RegistrationErrorType,
  message: string,
  details?: string,
  affectedRows?: number[]
): RegistrationError => ({
  type,
  message,
  details,
  affectedRows: affectedRows ? Object.freeze([...affectedRows]) : undefined,
});

/**
 * ExportErrorを生成する
 * @param type エラータイプ
 * @param message メッセージ
 * @param details 詳細
 * @param code エラーコード
 * @returns ExportError
 */
export const createExportError = (
  type: ExportErrorType,
  message: string,
  details?: string,
  code?: string
): ExportError => ({
  type,
  message,
  details,
  code,
});
